import { exists } from 'base/helpers';
import {
  BlocksService,
  FontFaceMetadata,
  FontFamilyMetadata,
} from 'blocks/blocks_service';

import { Project } from 'blocks/editor/model';
import { FONT_WEIGHTS_BODY } from 'blocks/editor/styleguide/fonts';
import * as TypeSettings from 'blocks/editor/styleguide/type_settings';
import { minBy } from 'lodash';

/**
 * Given a font family and a list of font weights we'd like to load, returns
 * font faces that we can actually load.
 *
 * e.g. if the font family only has weights 400 and 500, and we request the
 * preferred weight of 700, we'll end up with 500 since it's closest to 700.
 */
export const getFontFacesToLoad = (
  fontFamily: FontFamilyMetadata,
  preferredWeights: number[],
): FontFaceMetadata[] => {
  const weightsToLoad = new Set<string>();

  return preferredWeights
    .map((preferredWeight) => {
      const closestFontFace = getClosestFontFace(fontFamily, preferredWeight);

      // Avoid returning duplicates. (e.g. if the family has weight 400, and
      // preferred is 300 and 500, we only want to return 400 once)
      if (weightsToLoad.has(closestFontFace.weight)) {
        return;
      }
      weightsToLoad.add(closestFontFace.weight);

      return closestFontFace;
    })
    .filter(exists);
};

const MAX_WEIGHT_DIFFERENCE = 100;
export const isValidFontWeight = (
  preferredWeight: number,
  fontFamily: FontFamilyMetadata,
): boolean => {
  return fontFamily.fontFaces.some(({ weight, style }) => {
    const numericWeight = Number.parseInt(weight);
    return (
      style === 'normal' &&
      numericWeight >= preferredWeight &&
      Math.abs(numericWeight - preferredWeight) <= MAX_WEIGHT_DIFFERENCE
    );
  });
};

/**
 * Finds the closest matching font face within a font family based on a preferred font weight.
 *  The function prioritizes font faces with:
 *  1. weight >= preferredWeight, only weight match this condition considered as a valid font face
 *  2. If no such face exists, it falls back to any `'normal'` style, regardless of weight.
 */
export const getClosestFontFace = (
  fontFamily: FontFamilyMetadata,
  preferredWeight: number,
): FontFaceMetadata => {
  const parseWeight = (weight: string) => Number.parseInt(weight);

  const candidates = fontFamily.fontFaces.filter(({ style, weight }) => {
    const numericWeight = parseWeight(weight);
    return (
      style === 'normal' &&
      numericWeight >= preferredWeight &&
      isValidFontWeight(preferredWeight, fontFamily)
    );
  });

  const closestValidFontFace = minBy(candidates, ({ weight }) =>
    Math.abs(parseWeight(weight) - preferredWeight),
  );

  if (closestValidFontFace) {
    return closestValidFontFace;
  }

  const closestFontFace = minBy(
    fontFamily.fontFaces.filter(({ style }) => style === 'normal'),
    ({ weight }) => Math.abs(Number.parseInt(weight) - preferredWeight),
  );

  if (!closestFontFace) {
    throw new Error("Couldn't find any suitable font face");
  }

  return closestFontFace;
};

const SOURCE_CODE_MAP: Record<'Google' | 'Monotype', string> = {
  Google: 'G',
  Monotype: 'M',
};
export async function getFontsFromConcept({
  conceptUuid,
  blocksService,
  project,
  sourceFilter,
}: {
  blocksService: BlocksService;
  project: Project['state'];
  conceptUuid: string;
  sourceFilter: 'Monotype' | 'Google' | 'All';
}) {
  const concept = project.concepts.find((c) => c.uuid === conceptUuid);
  if (!concept) {
    throw new Error('Concept not found');
  }

  const [headingFontFamily, bodyFontFamily] = await Promise.all([
    blocksService.getFontFamily(concept.fontFamilies.heading.slug),
    blocksService.getFontFamily(concept.fontFamilies.body.slug),
  ]);

  const fontFamiliesToInclude = [
    {
      fontFamily: headingFontFamily,
      fontFaces: getFontFacesToLoad(headingFontFamily, [
        TypeSettings.HEADING_FONT_WEIGHTS[concept.typeSettings.weight],
      ]),
    },
    {
      fontFamily: bodyFontFamily,
      fontFaces: getFontFacesToLoad(bodyFontFamily, FONT_WEIGHTS_BODY),
    },
  ];

  return fontFamiliesToInclude
    .filter(({ fontFamily }) =>
      sourceFilter !== 'All'
        ? fontFamily.source === SOURCE_CODE_MAP[sourceFilter]
        : true,
    )
    .map(({ fontFamily, fontFaces }) => ({
      family: fontFamily.name,
      fontFaces,
    }));
}
